import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { ObjectId } from 'mongodb';
import { BehaviorSubject, Observable } from 'rxjs';

import { ENDPOINTS, URLS } from '@shared/constants';
import { CommonResponseDTO, IPhoneNumber } from '@shared/interfaces';
import { generateURL } from '@shared/utils';

import { LoggedUserService, UserInfoResponseDTO } from '../../../auth/services';
import { ITestEmail } from '../send-test-email/send-test-email.component';

export interface NotificationEmailConfigDTO {
  user: string;
  password: string;
  host: string;
  port: number;
  from: string;
  from_email: string;
  tls?: string;
  secure: boolean;
}
export interface PushNotificationConfigDTO {
  appId: string;
  apiKey: string;
}
export interface NotificationSMSConfigDTO {
  username: string;
  password: string;
  source: string;
  gateway: string;
}

export interface NotificationConfigResponseDTO {
  isNotificationEnable: boolean;
  isNotificationEnableViaEmail: boolean;
  isPushNotificationEnable?: boolean;
  pushNotificationConfig?: PushNotificationConfigDTO;
  emailGatewayConfig: NotificationEmailConfigDTO;
  smsGatewayConfig?: NotificationSMSConfigDTO;
  isSMTPConfigurationValid?: boolean;
  tenantId?: string | ObjectId;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationConfigService {
  public readonly dataStore =
    new BehaviorSubject<NotificationConfigResponseDTO>(undefined);

  private loggedUser: UserInfoResponseDTO;

  constructor(
    private http: HttpClient,
    private loggedUserService: LoggedUserService
  ) {
    this.loggedUserService.dataStore.subscribe((loggedUser) => {
      this.loggedUser = loggedUser;
    });
  }

  fetchSystemNotificationConfig(): Observable<
    CommonResponseDTO<NotificationConfigResponseDTO>
  > {
    return this.http
      .get<CommonResponseDTO<NotificationConfigResponseDTO>>(
        URLS.NOTIFICATION_CONFIG_NOTIFICATION
      )
      .pipe(
        tap(({ data }) => {
          this.dataStore.next(data);
        })
      );
  }

  findEmailConfigurationByTenantId(): Observable<
    CommonResponseDTO<NotificationConfigResponseDTO>
  > {
    const url = generateURL({
      endpoint: ENDPOINTS.NOTIFICATION_CONFIG_EMAIL,
      params: { tenantId: this.loggedUser.tenant._id.toString() },
    });
    return this.http.get<CommonResponseDTO<NotificationConfigResponseDTO>>(url);
  }

  findDefaultEmailConfiguration(): Observable<
    CommonResponseDTO<NotificationConfigResponseDTO>
  > {
    return this.http.get<CommonResponseDTO<NotificationConfigResponseDTO>>(
      URLS.NOTIFICATION_CONFIG_EMAIL_DEFAULT
    );
  }

  getEmailPassword(): Observable<CommonResponseDTO<any>> {
    return this.http.get<CommonResponseDTO<any>>(
      URLS.NOTIFICATION_CONFIG_GET_EMAIL_PASSWORD
    );
  }

  getSmsPassword(): Observable<CommonResponseDTO<any>> {
    return this.http.get<CommonResponseDTO<any>>(
      URLS.NOTIFICATION_CONFIG_GET_SMS_PASSWORD
    );
  }

  getPushNotificationKey(): Observable<CommonResponseDTO<any>> {
    return this.http.get<CommonResponseDTO<any>>(
      URLS.NOTIFICATION_CONFIG_GET_PUSH_NOTIFICATION_KEY
    );
  }

  updateSystemNotificationsConfig(
    body: NotificationConfigResponseDTO
  ): Observable<CommonResponseDTO<NotificationConfigResponseDTO>> {
    return this.http
      .put<CommonResponseDTO<NotificationConfigResponseDTO>>(
        URLS.NOTIFICATION_CONFIG_NOTIFICATION,
        body
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  updateTenantNotificationsConfig(
    body: NotificationConfigResponseDTO
  ): Observable<CommonResponseDTO<NotificationConfigResponseDTO>> {
    return this.http
      .put<CommonResponseDTO<NotificationConfigResponseDTO>>(
        URLS.NOTIFICATION_CONFIG_TENANT_EMAIL,
        body
      )
      .pipe(
        tap((res) => {
          this.dataStore.next(res.data);
        })
      );
  }

  sendTestEmail(data: ITestEmail): Observable<CommonResponseDTO<ITestEmail>> {
    return this.http.post<CommonResponseDTO<ITestEmail>>(
      URLS.NOTIFICATION_CONFIG_TEST_EMAIL,
      data
    );
  }

  sendTenantTestEmail(
    data: ITestEmail
  ): Observable<CommonResponseDTO<ITestEmail>> {
    return this.http.post<CommonResponseDTO<ITestEmail>>(
      URLS.NOTIFICATION_CONFIG_TENANT_TEST_EMAIL,
      data
    );
  }

  sendTestSMS(phone_number: IPhoneNumber): Observable<CommonResponseDTO<void>> {
    return this.http.post<CommonResponseDTO<void>>(
      URLS.NOTIFICATION_CONFIG_TEST_SMS,
      phone_number
    );
  }
}
